import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faClock } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import {
	ContactChannels,
	ContactEditModal,
	mapListPersonToChannelData,
	useContactChannelActions,
} from "@/components/ContactChannels";
import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";
import { InfoCardPopover } from "@/components/InfoCardPopover";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { ListPerson } from "../../schema/list";
import { buildLeadInfoCardRows } from "./leadInfoCardRows";
import { NameColumnCrmBadges } from "./NameColumnCrmBadges";
import { isDummyProspect, SelectProspectCell } from "./SelectProspectCell";
import { isFoundAutomatically } from "./trackerFindPersonRow";

/**
 * The lead list's name cell: the placeholder's "Select prospect" button, or the person with their
 * info card, CRM badges and contact channels. A person a tracker list's automatic search found
 * says so under the name (PREC-4399). Moved out of allColumns.tsx, which re-exports it.
 */
export const EditableNameColumn: FunctionComponent<{
	row: ListPerson;
	handleCopyClick: (value: string) => void;
	orderTitle: string;
	/** Open the lead's detail panel — wired to the channel icons' "open panel" affordance. */
	onOpenDetail?: (row: ListPerson) => void;
}> = ({ row, handleCopyClick, onOpenDetail }) => {
	const { t } = useTranslation();
	const [showEditModal, setShowEditModal] = useState(false);
	// Bumping this remounts the hover tooltip so it closes the moment the edit
	// modal opens — the Tooltip is hover-driven and never gets a mouseleave once
	// the modal backdrop covers the name.
	const [tooltipNonce, setTooltipNonce] = useState(0);
	const { update, isUpdating } = useContactChannelActions("list");

	const openEdit = () => {
		setTooltipNonce((nonce) => nonce + 1);
		setShowEditModal(true);
	};

	// Show "Select prospect" button for dummy prospects
	if (isDummyProspect(row)) {
		return <SelectProspectCell row={row} />;
	}

	return (
		<div className="relative flex w-full max-w-[250px] flew-row justify-between items-center">
			<Tooltip
				key={`name-${tooltipNonce}`}
				content={
					<InfoCardPopover
						kind="lead"
						onCopy={handleCopyClick}
						onEdit={row.prospect ? openEdit : undefined}
						rows={buildLeadInfoCardRows(row)}
					/>
				}
			>
				<div className="cursor-pointer mr-1 max-w-[150px] overflow-hidden flex items-center">
					{row.prospect?.snoozed ? (
						<Icon
							className="w-[20px] h-[20px] mt-0.5 text-text-disabled mr-1 float-left shrink-0"
							icon={faClock}
						/>
					) : row.prospect?.image ? (
						<HideOnErrorImg
							alt={row.prospect?.name ?? ""}
							className="w-[24px] h-[24px] float-left rounded-full mr-1 shrink-0"
							referrerPolicy="no-referrer"
							src={row.prospect?.image}
						/>
					) : (
						""
					)}
					<span className="flex min-w-0 flex-1 flex-col">
						<span className="block min-w-0 truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors">
							{row.prospect?.name}
						</span>
						{isFoundAutomatically(row) && (
							<span className="truncate text-[11px] leading-tight text-text-meta">
								{t("defineIcps.trackerFindPerson.row.foundAutomatically")}
							</span>
						)}
					</span>
				</div>
			</Tooltip>
			<div className="flex flex-row">
				<NameColumnCrmBadges row={row} />
				{row.prospect && (
					<ContactChannels
						data={mapListPersonToChannelData(row)}
						domain="list"
						onCopy={handleCopyClick}
						onOpenPanel={onOpenDetail ? () => onOpenDetail(row) : undefined}
					/>
				)}
			</div>
			{row.prospect && showEditModal && (
				// Isolate the modal's clicks so they don't bubble to the row and toggle
				// the detail view (mirrors the wrapper in ContactChannels).
				<div onClick={(e) => e.stopPropagation()}>
					<ContactEditModal
						data={mapListPersonToChannelData(row)}
						isLoading={isUpdating}
						isOpen={showEditModal}
						onClose={() => setShowEditModal(false)}
						onSubmit={(values) => update(values)}
					/>
				</div>
			)}
		</div>
	);
};
